import { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCollaboration } from "@/contexts/CollaborationContext";
import { Eye, EyeOff, X } from "lucide-react";

const USER_COLORS = [
  "#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16",
];

function getColor(userId) {
  let hash = 0;
  for (let i = 0; i < (userId || "").length; i++) hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

export default function FollowMode({ onScrollToFrame }) {
  const {
    members, connected, followingUser, setFollowingUser,
    followedViewport, viewportStates, emitViewportState
  } = useCollaboration();

  const otherMembers = members.filter(m => m.user_id !== undefined);

  const handleAvatarClick = useCallback((userId) => {
    if (followingUser === userId) {
      setFollowingUser(null);
    } else {
      setFollowingUser(userId);
    }
  }, [followingUser, setFollowingUser]);

  // Auto-scroll to followed user's selected frame
  if (followedViewport?.selected_frame_id && onScrollToFrame) {
    // Defer to avoid re-render loop
    setTimeout(() => onScrollToFrame(followedViewport.selected_frame_id), 0);
  }

  if (!connected || otherMembers.length === 0) return null;

  const followedMember = followingUser ? members.find(m => m.user_id === followingUser) : null;

  return (
    <>
      {/* Collaborator avatars */}
      <div className="flex items-center gap-1.5">
        {otherMembers.map((m) => {
          const color = getColor(m.user_id);
          const isFollowed = followingUser === m.user_id;
          const hasViewport = !!viewportStates[m.user_id];
          return (
            <button
              key={m.user_id}
              onClick={() => handleAvatarClick(m.user_id)}
              title={`${m.name}${isFollowed ? " (following)" : hasViewport ? " — click to follow" : ""}`}
              className="relative group"
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white transition-all ${
                  isFollowed ? "ring-2 ring-offset-1 ring-offset-[var(--bg-app)] scale-110" : "hover:scale-110"
                }`}
                style={{
                  backgroundColor: color,
                  ringColor: isFollowed ? color : undefined,
                }}
              >
                {(m.name || "?").charAt(0).toUpperCase()}
              </div>
              {isFollowed && (
                <Eye size={8} className="absolute -bottom-0.5 -right-0.5 text-white bg-black/70 rounded-full p-0.5" />
              )}
              {/* Tooltip */}
              <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap px-2 py-1 bg-black/80 text-white text-[9px] rounded z-50">
                {m.name}
              </div>
            </button>
          );
        })}
      </div>

      {/* Following banner */}
      <AnimatePresence>
        {followedMember && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg"
            style={{
              backgroundColor: "var(--bg-surface)",
              border: `2px solid ${getColor(followedMember.user_id)}`,
            }}
          >
            <Eye size={14} style={{ color: getColor(followedMember.user_id) }} />
            <span className="text-xs text-[var(--text-primary)]">
              Following <strong>{followedMember.name}</strong>
            </span>
            <button
              onClick={() => setFollowingUser(null)}
              className="ml-1 p-0.5 hover:bg-[var(--bg-hover)] rounded-full"
            >
              <X size={12} className="text-[var(--text-muted)]" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Frame indicator pip — shows which frame another user is viewing */
export function CollaboratorPip({ frameId }) {
  const { viewportStates, members } = useCollaboration();

  const viewers = members.filter(m =>
    viewportStates[m.user_id]?.selected_frame_id === frameId
  );

  if (viewers.length === 0) return null;

  return (
    <div className="absolute top-1 right-1 flex -space-x-1 z-10">
      {viewers.slice(0, 3).map((m) => (
        <div
          key={m.user_id}
          className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white border border-black/30"
          style={{ backgroundColor: getColor(m.user_id) }}
          title={m.name}
        >
          {(m.name || "?").charAt(0)}
        </div>
      ))}
    </div>
  );
}
